import React from 'react';
import ReactDOM from 'react-dom/client';
import { Sentry } from './lib/sentry'; // side-effect: Sentry.init (DSN-guarded)
import { SpeedInsights } from '@vercel/speed-insights/react';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from './context/ThemeContext';
import { AdminProvider } from './context/AdminContext';
import { AuthProvider } from './context/AuthContext';
import { AuthModalProvider } from './context/AuthModalContext';
import { installChunkReloadHandler } from './lib/chunkReload';
import App from './App';
import './index.css';

// Auto-recover from stale-deploy chunk errors (old tab + new deploy → lazy
// import() gets index.html instead of JS). Must run before the first render.
installChunkReloadHandler();

// ── React Query client ───────────────────────────────────────────────
// `staleTime: 60s`   → sensible default; time-sensitive hooks (events, hukamnama)
//                      set their own lower value. Static data (ragis, gurudwaras)
//                      can override upward if needed.
// `gcTime:    24h`   → query data lives in cache (and persister) for a day,
//                      so a return visit hydrates instantly with last-known data.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 24 * 60 * 60 * 1000,
      // Never retry 401/403 — the token is simply not ready yet or the user
      // is not authorised; retrying just fires 2 more slow failing requests.
      retry: (failureCount, error) => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

// ── Persistence: localStorage, ~24h max age, busted on app version change ──
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'kd:rq-cache',
  // Default JSON serialiser — fine for our payloads (small, no Maps/Dates).
  throttleTime: 1000,
});

const APP_BUSTER = '1'; // bump to invalidate the on-disk cache after schema changes

ReactDOM.createRoot(document.getElementById('root')!, {
  // Route React render/commit errors to Sentry (no-op if DSN unset).
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
}).render(
  <React.StrictMode>
    <HelmetProvider>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          maxAge: 24 * 60 * 60 * 1000,
          buster: APP_BUSTER,
          // Don't persist auth-bearing or user-specific queries (keys starting
          // with 'me', 'profile', 'favourites', 'admin', or 'amrit-sanchar-register')
          // — these may contain personal data that must not survive in localStorage.
          dehydrateOptions: {
            shouldDehydrateQuery: (query) => {
              if (query.state.status !== 'success') return false;
              const head = String(query.queryKey[0] ?? '');
              const PRIVATE = ['me', 'profile', 'favourites', 'admin', 'claim'];
              if (PRIVATE.some((p) => head === p || head.startsWith(`${p}-`))) return false;
              return true;
            },
          },
        }}
      >
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <AuthModalProvider>
                <AdminProvider>
                  <App />
                  <SpeedInsights />
                </AdminProvider>
              </AuthModalProvider>
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </PersistQueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>,
);
